const STORAGE_KEY = 'la-bella-react-cart';
const ORDERS_KEY = 'la-bella-react-orders';
const MAX_LINE_QUANTITY = 99;
const MAX_CART_LINES = 50;
const MAX_STORED_ORDERS = 20;
const siteBase = import.meta.env.VITE_SITE_BASE || '';
const defaultStorefrontApi = '/backend/storefront-api.php';
const storefrontApi = import.meta.env.VITE_STOREFRONT_API_URL || defaultStorefrontApi;

async function loadStorefrontData() {
  try {
    const response = await fetch(`${storefrontApi}?ts=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Storefront backend unavailable');
    return await response.json();
  } catch {
    // Only pulled in when the backend is down — keeps the ~96 kB snapshot
    // out of the main bundle.
    return (await import('../data/storefrontData.json')).default;
  }
}

function activeProducts(data) {
  return (data.products || []).filter((product) => product.isActive);
}

export function assetUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const cleanBase = siteBase.replace(/\/$/, '');
  const cleanPath = String(path).replace(/^\//, '');
  return cleanBase ? `${cleanBase}/${cleanPath}` : `/${cleanPath}`;
}

export const storefrontService = {
  bootstrap: async () => structuredClone((await loadStorefrontData()).bootstrap),
  content: async () => structuredClone((await loadStorefrontData()).content),
  products: async (params = {}) => productsPayload(params, await loadStorefrontData()),
  product: async (id) => productPayload(id, await loadStorefrontData()),
  cart: async () => {
    const data = await loadStorefrontData();
    return { cart: buildCart(readCartLines(data), data) };
  },
  cartAction: async (body) => cartAction(body, await loadStorefrontData()),
  validateCart: async () => validateCartStock(await loadStorefrontData()),
  // Customer details are validated in the UI but intentionally never passed
  // here: without a backend, PII must not be persisted anywhere client-side.
  placeOrder: async () => placeOrder(await loadStorefrontData()),
  orders: async () => readOrders(),
};

function productsPayload(params, data) {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );
  const type = cleanParams.type || '';
  const id = Number(cleanParams.id || 0);
  const search = String(cleanParams.search || '').trim().toLowerCase();
  const sort = cleanParams.sort || 'best-selling';
  const limit = Math.max(1, Number(cleanParams.limit || 48));
  const saleOnly = cleanParams.sale_only === '1';

  const productsForFilters = activeProducts(data);
  let products = [...productsForFilters];

  if (type && id) {
    products = products.filter((product) => matchesCategory(product, type, id));
  }

  if (search) {
    products = products.filter((product) => {
      const haystack = [product.name, product.shortDescription, product.description].join(' ').toLowerCase();
      return haystack.includes(search);
    });
  }

  if (saleOnly) {
    products = products.filter((product) => product.hasDiscount);
  }

  products.sort(sortProducts(sort));

  return structuredClone({
    category: {
      id: id || null,
      type: type || null,
      title: categoryTitle(type, id, data),
    },
    products: products.slice(0, limit),
    filters: filterPayload(productsForFilters),
  });
}

function productPayload(id, data) {
  const payload = data.productDetails[String(id)];
  if (!payload) {
    throw new Error('Product not found');
  }

  const clone = structuredClone(payload);
  // Gallery photos (id !== 0) are stored under product_photos/, but the
  // exported data records them at the uploads root. Remap to the real path.
  clone.product.photos = (clone.product.photos || []).map((photo) => (
    photo.id === 0 ? photo : { ...photo, image: `assets/uploads/product_photos/${photo.photo}` }
  ));
  return clone;
}

function matchesCategory(product, type, id) {
  if (type === 'top-category') return product.category?.topId === id;
  if (type === 'mid-category') return product.category?.midId === id;
  if (type === 'end-category') return product.category?.endId === id;
  return true;
}

function categoryTitle(type, id, data) {
  if (!type || !id) return 'Products';

  for (const topCategory of data.bootstrap.navigation) {
    if (type === 'top-category' && topCategory.id === id) return topCategory.name;

    for (const midCategory of topCategory.children || []) {
      if (type === 'mid-category' && midCategory.id === id) return midCategory.name;

      for (const endCategory of midCategory.children || []) {
        if (type === 'end-category' && endCategory.id === id) return endCategory.name;
      }
    }
  }

  return 'Products';
}

function sortProducts(sort) {
  return (a, b) => {
    if (sort === 'a-z') return a.name.localeCompare(b.name);
    if (sort === 'z-a') return b.name.localeCompare(a.name);
    if (sort === 'price-low-high') return a.currentPrice - b.currentPrice;
    if (sort === 'price-high-low') return b.currentPrice - a.currentPrice;
    if (sort === 'latest') return b.id - a.id;
    return b.totalView - a.totalView;
  };
}

function filterPayload(products) {
  const prices = products.map((product) => product.currentPrice);
  return {
    priceRange: {
      min: prices.length ? Math.min(...prices) : 0,
      max: prices.length ? Math.max(...prices) : 0,
    },
    availability: {
      inStock: products.filter((product) => product.quantity > 0).length,
      outStock: products.filter((product) => product.quantity <= 0).length,
    },
  };
}

function readCartLines(data) {
  if (typeof window === 'undefined') return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    // localStorage is user-editable: accept only well-formed lines for known
    // products, coerce every field to a safe integer, and cap totals.
    return parsed
      .filter((line) => line && typeof line === 'object')
      .map((line) => ({
        productId: toPositiveInt(line.productId),
        sizeId: toNonNegativeInt(line.sizeId),
        colorId: toNonNegativeInt(line.colorId),
        quantity: clampQuantity(line.quantity),
      }))
      .filter((line) => line.productId && line.quantity && getProduct(line.productId, data))
      .slice(0, MAX_CART_LINES);
  } catch {
    return [];
  }
}

function toPositiveInt(value) {
  const number = Math.trunc(Number(value));
  return Number.isSafeInteger(number) && number > 0 ? number : 0;
}

function toNonNegativeInt(value) {
  const number = Math.trunc(Number(value));
  return Number.isSafeInteger(number) && number >= 0 ? number : 0;
}

function clampQuantity(value) {
  const number = Math.trunc(Number(value));
  if (!Number.isSafeInteger(number) || number < 1) return 0;
  return Math.min(number, MAX_LINE_QUANTITY);
}

function writeCartLines(lines) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  }
}

function cartAction(body, data) {
  const lines = readCartLines(data);
  const action = body.action || '';
  const productId = toPositiveInt(body.productId);
  const sizeId = toNonNegativeInt(body.sizeId);
  const colorId = toNonNegativeInt(body.colorId);
  const quantity = clampQuantity(body.quantity) || 1;

  if (action === 'clear') {
    writeCartLines([]);
    return { cart: buildCart([], data) };
  }

  if (!['add', 'update', 'remove'].includes(action)) {
    throw new Error('Invalid cart action');
  }

  const product = getProduct(productId, data);
  if (!product) throw new Error('Product not found');

  const index = lines.findIndex((line) => (
    line.productId === productId && line.sizeId === sizeId && line.colorId === colorId
  ));

  if (action === 'add') {
    const currentQuantity = index >= 0 ? lines[index].quantity : 0;
    const nextQuantity = Math.min(currentQuantity + quantity, MAX_LINE_QUANTITY);
    assertStock(product, nextQuantity);

    if (index >= 0) lines[index].quantity = nextQuantity;
    else if (lines.length >= MAX_CART_LINES) throw new Error('Cart is full');
    else lines.push({ productId, sizeId, colorId, quantity });
  }

  if (action === 'update' && index >= 0) {
    assertStock(product, quantity);
    lines[index].quantity = quantity;
  }

  if (action === 'remove' && index >= 0) {
    lines.splice(index, 1);
  }

  writeCartLines(lines);
  return { cart: buildCart(lines, data) };
}

function buildCart(lines, data) {
  const items = lines
    .map((line) => cartItem(line, data))
    .filter(Boolean);
  const quantity = items.reduce((total, item) => total + item.quantity, 0);
  const subtotal = items.reduce((total, item) => total + item.subtotal, 0);

  return { items, quantity, subtotal };
}

function cartItem(line, data) {
  const detail = data.productDetails[String(line.productId)];
  const product = detail?.product;
  if (!product) return null;

  const size = product.sizes?.find((item) => item.id === line.sizeId);
  const color = product.colors?.find((item) => item.id === line.colorId);
  const price = Math.max(0, Number(size?.currentPrice || product.currentPrice || 0));
  const quantity = Math.max(1, Math.min(clampQuantity(line.quantity) || 1, Math.max(product.quantity, 1)));

  return {
    lineKey: [line.productId, line.sizeId, line.colorId].join(':'),
    productId: line.productId,
    sizeId: line.sizeId,
    sizeName: size?.name || '',
    colorId: line.colorId,
    colorName: color?.name || '',
    name: product.name,
    image: product.image,
    quantity,
    price,
    subtotal: Number((price * quantity).toFixed(2)),
  };
}

function getProduct(productId, data) {
  return data.productDetails[String(productId)]?.product;
}

function assertStock(product, quantity) {
  if (product.quantity <= 0) {
    throw new Error('This product is out of stock');
  }

  if (quantity > product.quantity) {
    throw new Error(`Only ${product.quantity} item(s) in stock`);
  }
}

/* ------------------------------------------------------------------
   Checkout: stock revalidation + order placement.
   Order records deliberately contain NO personal data (no name, email,
   phone, or address) — only an order reference and the purchased lines.
   ------------------------------------------------------------------ */

function validateCartStock(data) {
  const cart = buildCart(readCartLines(data), data);
  const issues = [];

  for (const item of cart.items) {
    const product = getProduct(item.productId, data);
    const available = product ? Math.max(product.quantity, 0) : 0;
    if (!product || available <= 0) {
      issues.push({ name: item.name, requested: item.quantity, available: 0 });
    } else if (item.quantity > available) {
      issues.push({ name: item.name, requested: item.quantity, available });
    }
  }

  return { cart, issues, ok: cart.items.length > 0 && issues.length === 0 };
}

function placeOrder(data) {
  const { cart, issues, ok } = validateCartStock(data);

  if (!cart.items.length) {
    throw new Error('Your cart is empty');
  }

  if (!ok) {
    const detail = issues
      .map((issue) => `${issue.name} (requested ${issue.requested}, available ${issue.available})`)
      .join('; ');
    throw new Error(`Some items are no longer available: ${detail}`);
  }

  const order = {
    reference: orderReference(),
    placedAt: new Date().toISOString(),
    items: cart.items.map((item) => ({
      productId: item.productId,
      name: item.name,
      sizeName: item.sizeName,
      colorName: item.colorName,
      quantity: item.quantity,
      price: item.price,
      subtotal: item.subtotal,
    })),
    quantity: cart.quantity,
    subtotal: cart.subtotal,
  };

  const orders = [order, ...readOrders()].slice(0, MAX_STORED_ORDERS);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(ORDERS_KEY, JSON.stringify(orders));
  }
  writeCartLines([]);

  return { order, cart: buildCart([], data) };
}

function readOrders() {
  if (typeof window === 'undefined') return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(ORDERS_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((order) => order && typeof order === 'object' && typeof order.reference === 'string')
      .slice(0, MAX_STORED_ORDERS);
  } catch {
    return [];
  }
}

function orderReference() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  let code = '';
  for (const byte of bytes) code += alphabet[byte % alphabet.length];
  return `LB-${code.slice(0, 4)}-${code.slice(4)}`;
}
